import React, { useEffect, useRef, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Dropdown, Input, toastMessage, AddRemove, Button, Loader } from "../utils/index";
import { useCheckpointTypes, useStateOffices, useUserDetail, useUserPermissions } from "../../services/hooks";
import { capitalizeCamelCase, convertToCamelCase } from "../../services/functions";
import { useAddOrgMutation, useUpdateThemeMutation, useGetOrgsQuery } from "../../api/userSlice";
import { ThemeEditor } from "../Settings/Settings";
import { registerLeaveGuard } from "../../services/navigation";
import UploadFileButton from "../utils/index";
import { Checkbox, ColorPicker } from "../utils/index";
import { base64Convert } from "../../services/functions";
import "./Company.css";
import { assignDeep } from "../Dynamic/DynamicForm";
import Geofence from "../utils/Geofence";
import { buttonClasses } from "../utils/theme";
import { useLocation } from "react-router-dom";

const defaultForm = {
	orgName: "",
	representativeName: "",
	representativeEmail: "",
	representativeMobile: "",
	address: "",
	state: "",
	city: "",
	pincode: "",
	pan: "",
	gstin: "",
	updateId: undefined,
	title: "",
	theme: {
		primaryColor: "#ff7200",
		selectedTheme: "lightMode",
	},
	logo: {
		fileData: "",
		docType: "",
		fileName: "",
	},
	favIcon: {
		fileData: "",
		docType: "",
		fileName: "",
	},
	permissions: {},
	hhtImei: [{ imei: "" }],
	statIcon: {
		total: {
			fileData: "",
			docType: "",
			fileName: "",
		},
		todayIn: {
			fileData: "",
			docType: "",
			fileName: "",
		},
		todayOut: {
			fileData: "",
			docType: "",
			fileName: "",
		},
		currentIn: {
			fileData: "",
			docType: "",
			fileName: "",
		},
	},
};

// Display only: stored keys look like `<timestamp>_<original name>`.
function displayFileName(fileName) {
	if (!fileName) return fileName;
	const friendly = String(fileName).replace(/^\d+_/, "");
	return friendly || fileName;
}

// The only details an edit can change (everything else is read-only); used to detect unsaved edits.
function detailsSnapshot(form) {
	return JSON.stringify({ permissions: form?.permissions || {}, hhtImei: form?.hhtImei || [] });
}

const EDIT_TABS = ["details", "theme"];

function getPath(obj, path) {
	return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

// Edit mode keeps only what the Update button sends (plus the read-only details). The theme is
// edited and saved by the embedded ThemeEditor, never through this form.
function fillUpdateData(data) {
	return {
		orgName: data.name,
		representativeName: data.representativeName,
		representativeEmail: data.representativeEmail,
		representativeMobile: data.representativeMobile,
		address: data.address,
		state: data.state,
		city: data.city,
		pincode: data.pincode,
		pan: data.pan,
		gstin: data.gstin,
		updateId: data._id,
		permissions: data.permissions || {},
		hhtImei: data.hhtImei || [{ imei: "" }],
	};
}
let orgPerms = {
	multiTagSupport: {
		value: true,
		strict: false,
		dependancy: [],
	},
	thirdPartyTripSetup: {
		value: true,
		strict: false,
		dependancy: [],
	},
	thirdPartyTripStatusUpdate: {
		value: true,
		strict: false,
		dependancy: [],
	},
	assetMappingSupport: {
		value: true,
		strict: false,
		dependancy: [],
	},
	assetAdd: {
		value: true,
		strict: false,
		dependancy: [],
	},
	assetScan: {
		value: true,
		strict: false,
		dependancy: [],
	},
	createTripsThroughStrayFastags: {
		value: true,
		strict: false,
		dependancy: [],
	},
	managePlant: {
		value: true,
		strict: false,
		dependancy: [],
	},
};

const DEFAULT_CHEKPOINTS = [
	{ displayName: "Access", checkpointType: "access" },
	{ displayName: "Gate In", checkpointType: "gateIn" },
	{ displayName: "Bay", checkpointType: "bay" },
	{ displayName: "Gate Out", checkpointType: "gateOut" },
];
const AddCompanyForm = () => {
	const location = useLocation();
	const { id } = useParams();
	const isUpdate = location.pathname.includes("edit");
	const stateData = location.state?.updateData;
	// Listing state is only trusted when it belongs to the org in the URL; otherwise fetch by id.
	const stateMatches = !!stateData && (!id || String(stateData._id) === String(id));
	const { userDetail } = useUserDetail();
	const { userPermissions, isFetched: permissionsFetched } = useUserPermissions();
	const isSuperAdmin = userDetail?.userType === "superAdmin";
	// Same rule as the Edit action on the companies list: superAdmin, or updateOrganization on
	// the user's own organization. Anything else is refused before the org is fetched.
	const editTargetId = id || stateData?._id;
	const canOpenEdit = isSuperAdmin || (!!userPermissions?.updateOrganization?.value && !!editTargetId && String(editTargetId) === String(userDetail?.orgId));
	const needsFetch = isUpdate && !!id && !stateMatches;
	const {
		currentData: orgsResponse,
		isFetching: isOrgFetching,
		isError: isOrgError,
		isSuccess: isOrgSuccess,
	} = useGetOrgsQuery({ orgId: id }, { skip: !needsFetch || !canOpenEdit, refetchOnMountOrArgChange: true });
	const fetchedOrg = needsFetch && Array.isArray(orgsResponse?.data) ? orgsResponse.data.find((o) => String(o?._id) === String(id)) : undefined;
	const updateData = stateMatches ? stateData : fetchedOrg;
	const navigate = useNavigate();
	const [formData, setFormData] = useState(updateData ? fillUpdateData(updateData) : defaultForm);
	const initializedRef = useRef(!!updateData);
	// Edit mode: "Details" and "Theme" tabs save independently and never leave the page.
	const [activeTab, setActiveTab] = useState(() => {
		const requested = new URLSearchParams(location.search).get("tab");
		return EDIT_TABS.includes(requested) ? requested : "details";
	});
	const [savedDetails, setSavedDetails] = useState(() => (updateData ? detailsSnapshot(fillUpdateData(updateData)) : null));
	const [themeDirty, setThemeDirty] = useState(false);
	const [errors, setErrors] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const submittingRef = useRef(false);
	const [updateThemeTrigger] = useUpdateThemeMutation();
	// Only a super admin may change org permissions (backend rejects them otherwise).
	const permsLocked = isUpdate && !isSuperAdmin;
	const canEditOrgTheme = isSuperAdmin || !!userPermissions?.manageTheme?.value || !!userPermissions?.updateOrganization?.value;
	const editOrgId = id || updateData?._id;
	const {states, statesOptions} = useStateOffices({})
	const [customizeCheckpoint, setCustomizeCheckpoint] = useState(false);

	const { checkpointTypeOptions } = useCheckpointTypes({ orgId: "all" });
	const stateValue= statesOptions.find((_)=>_.value == formData.state)
	useEffect(() => {
		if (isUpdate && !id && !stateData) {
			navigate("/companies");
		}
	}, []);
	// Initialise once from the fetched org; later refetches must not overwrite user edits.
	useEffect(() => {
		if (!initializedRef.current && fetchedOrg) {
			initializedRef.current = true;
			const filled = fillUpdateData(fetchedOrg);
			setFormData(filled);
			setSavedDetails(detailsSnapshot(filled));
		}
	}, [fetchedOrg]);
	useEffect(() => {
		if (!needsFetch || isOrgFetching || initializedRef.current) return;
		if (isOrgError || (isOrgSuccess && !fetchedOrg)) {
			toastMessage(false, "Company not found");
			navigate("/companies");
		}
	}, [needsFetch, isOrgFetching, isOrgError, isOrgSuccess, fetchedOrg]);
	const detailsDirty = isUpdate && savedDetails !== null && detailsSnapshot(formData) !== savedDetails;
	useEffect(() => {
		if (!detailsDirty) return;
		return registerLeaveGuard({
			label: "Details",
			guardsPlantSwitch: false,
			message: "Discard unsaved company details and leave?",
			isBusy: () => submittingRef.current,
			onBusy: () => toastMessage(false, "Wait for the company details save to finish before leaving."),
		});
	}, [detailsDirty]);
	const handleTabChange = (e, next) => {
		if (next === activeTab) return;
		setActiveTab(next);
		// Reflect the tab in the URL without a router navigation: a navigation would hit the
		// leave guard while either tab has unsaved changes. Keep history's own state entry intact.
		try {
			const url = new URL(window.location.href);
			url.searchParams.set("tab", next);
			window.history.replaceState(window.history.state, "", url);
		} catch { /* URL sync is a convenience only. */ }
	};
	const handleInput = (e) => {
		const name = e.target.name;
		const value = e.target.value;
		if (name === "representativeMobile") {
			if (value.length <= 10) {
				setFormData({ ...formData, [name]: value });
			}
		} else {
			setFormData({ ...formData, [name]: value });
		}
	};
	const onDropdownChange = (option, name) => {
		setFormData({ ...formData, [name]: option?.value });
	};
	const handlePermChange = (e) => {
		const { name } = e.target;

		setFormData((old) => {
			let newData = JSON.parse(JSON.stringify(old));
			if (newData.permissions[name]) {
				delete newData.permissions[name];
			} else {
				newData.permissions[name] = orgPerms[name];
			}
			return newData;
		});
	};
	const handleCheckAllPerm = (e) => {
		const { checked } = e.target;
		setFormData((o) => ({
			...o,
			permissions: checked ? { ...orgPerms } : {},
		}));
	};

	let perms = [];
	for (let perm in orgPerms) {
		perms.push(
			<Checkbox
				key={perm}
				label={capitalizeCamelCase(perm)}
				id={perm}
				name={perm}
				checked={!!formData.permissions[perm]}
				onChange={(e) => {
					handlePermChange(e);
				}}
				disabled={(perm == "multiTagSupport" && updateData ? true : false) || permsLocked}
			/>
		);
	}

	// const onDropdownChange = (option) => {
	// 	let inputData = { ...formData };
	// 	inputData.orgId = option?.value || "";
	// 	setFormData(inputData);
	// };
	const [handleSubmit] = useAddOrgMutation();
	const handleFormSubmit = async () => {
		if (submittingRef.current) return;
		const valid = validateForm(formData);
		// console.log('error', valid);
		if (valid.success === false) {
			setErrors(valid.errors);
			return;
		} else {
			setErrors({});
			let payload = { ...formData, title: formData.title || formData.orgName };
			// console.log(payload.checkpoints)
			if (payload.checkpoints) {
				payload.checkpoints = payload.checkpoints.filter((_) => _.displayName).map((_) => ({ ..._, checkpointStatus: convertToCamelCase(_.displayName) }));
			}
			// console.log(payload)
			submittingRef.current = true;
			setIsSubmitting(true);
			try {
				const res = await handleSubmit(payload).unwrap();
				if (res?.error) {
					toastMessage(false, res?.message || "Failed to add company");
				} else {
					toastMessage(true, res?.message || "Company added successfully");
					handleClose();
				}
			} catch (err) {
				toastMessage(false, err?.data?.message || "Failed to add company");
			} finally {
				submittingRef.current = false;
				setIsSubmitting(false);
			}
		}
	};

	const handleUpdateSubmit = async () => {
		if (submittingRef.current) return;
		const valid = updateValidateForm(formData);
		if (valid.success === false) {
			setErrors(valid.errors);
			return;
		}

		// Theme fields are saved by the ThemeEditor (users/update-org-theme), never from here.
		const payload = {
			orgID: formData.updateId,
			hhtImei: formData.hhtImei,
		};
		if (isSuperAdmin) {
			payload.permissions = formData.permissions;
		}
		// console.log({payload})
		// What is being saved; edits made while the request is in flight stay unsaved.
		const submitted = detailsSnapshot(formData);
		submittingRef.current = true;
		setIsSubmitting(true);
		try {
			const res = await updateThemeTrigger({ ...payload }).unwrap();
			if (res?.error) {
				toastMessage(false, res?.message || "Organization Update Failed");
			} else {
				toastMessage(true, res?.message || "Organization updated successfully");
				// Stay on the page: the Theme tab may still hold its own unsaved changes.
				setSavedDetails(submitted);
			}
		} catch (err) {
			toastMessage(false, err?.data?.message || "Organization Update Failed");
		} finally {
			submittingRef.current = false;
			setIsSubmitting(false);
		}
	};
	const handleClose = () => {
		if (!isUpdate) setFormData(defaultForm);
		// onClose();
		navigate("/companies");
		// setFetchStats((old) => !old);
	};

	const handleThemeChange = async (name, value, themeValue) => {
		if (name == "logo" || name == "favIcon" || name.includes("statIcon")) {
			const result = value != "" ? await base64Convert(value) : value;
			setFormData((o) => {
				let n = JSON.parse(JSON.stringify(o));
				assignDeep(n, name, { fileData: result, docType: name, fileName: value?.name || "" });
				return n;
			});
		} else if (name === "primaryColor") {
			setFormData({ ...formData, theme: { ...formData.theme, primaryColor: value } });
		} else if (name === "selectedTheme") {
			if (value === true) {
				setFormData({ ...formData, theme: { ...formData.theme, selectedTheme: themeValue } });
			}
		} else {
			setFormData({ ...formData, [name]: value });
		}
	};

	// Edit mode no longer carries theme fields (the ThemeEditor validates and saves them itself),
	// and the remaining editable fields (HHT IMEI, permissions) have no required values.
	function updateValidateForm() {
		const errors = {};
		const success = true;
		return { errors, success };
	}

	function validateForm(formData) {
		const errors = {};

		let success = true;

		// Validate orgName
		if (!formData.orgName.trim()) {
			errors.orgName = "Company name is required";
			success = false;
		}

		// Validate representativeName
		if (!formData.representativeName.trim()) {
			errors.representativeName = "Representative name is required";
			success = false;
		}

		// Validate representativeEmail
		if (!formData.representativeEmail.trim()) {
			errors.representativeEmail = "Email is required";
			success = false;
		} else if (!/^\S+@\S+\.\S+$/.test(formData.representativeEmail)) {
			errors.representativeEmail = "Invalid email address";
			success = false;
		}

		// Validate representativeMobile
		if (!formData.representativeMobile.trim()) {
			errors.representativeMobile = "Mobile number is required";
			success = false;
		} else if (formData.representativeMobile.trim().length !== 10) {
			errors.representativeMobile = "Mobile number must be 10 digits";
			success = false;
		}

		// Validate address
		if (!formData.address) {
			errors.address = "Address is required";
			success = false;
		}

		// Validate state
		if (!formData.state) {
			errors.state = "State is required";
			success = false;
		}

		// Validate city
		if (!formData?.city) {
			errors.city = "City is required";
			success = false;
		}

		// Validate pincode
		if (!formData?.pincode) {
			errors.pincode = "Pincode is required";
			success = false;
		} else if (!/^\d{6}$/.test(formData.pincode.trim())) {
			errors.pincode = "Invalid pincode";
			success = false;
		}

		// Validate PAN
		if (!formData?.pan) {
			errors.pan = "PAN is required";
			success = false;
		} else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan.trim().toUpperCase())) {
			errors.pan = "Invalid PAN number";
			success = false;
		}

		// Validate GSTIN
		if (!formData?.gstin) {
			errors.gstin = "GSTIN is required";
			success = false;
		} else if (!/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}\d{1}Z[A-Z0-9]{1}$/.test(formData.gstin.trim().toUpperCase())) {
			errors.gstin = "Invalid GSTIN";
			success = false;
		}

		// Validate title
		// if (!formData.title) {
		// 	errors.title = "Title is required";
		// 	success = false;
		// }

		// Validate theme
		if (!formData.theme || typeof formData.theme !== "object") {
			errors.theme = "Theme data is missing or invalid";
			success = false;
		} else {
			// Validate primaryColor
			if (!formData.theme.primaryColor) {
				errors.theme = "Primary color is required";
				success = false;
			}
			// Validate selectedTheme
			if (!["lightMode", "darkMode"].includes(formData.theme.selectedTheme)) {
				errors.theme = "Invalid selected theme";
				success = false;
			}
		}

		// Validate logo
		// if (!formData.logo || typeof formData.logo !== "object") {
		// 	errors.logo = "Logo data is missing or invalid";
		// 	success = false;
		// } else {
		// 	// Validate fileData
		// 	if (!formData.logo.fileData) {
		// 		errors.logo = "Logo file data is required";
		// 		success = false;
		// 	}
		// 	// Validate docType
		// 	if (!formData.logo.docType) {
		// 		errors.logo = "Logo document type is required";
		// 		success = false;
		// 	}
		// }

		// Validate favIcon
		// if (!formData.favIcon || typeof formData.favIcon !== "object") {
		// 	errors.favIcon = "Favicon data is missing or invalid";
		// 	success = false;
		// } else {
		// 	// Validate fileData
		// 	if (!formData.favIcon.fileData) {
		// 		errors.favIcon = "Favicon file data is required";
		// 		success = false;
		// 	}
		// 	// Validate docType
		// 	if (!formData.favIcon.docType) {
		// 		errors.favIcon = "Favicon document type is required";
		// 		success = false;
		// 	}
		// }
		//validate geofence

		return { errors, success };
	}

	// Add mode only: preview a picked file from its data URL (edit mode uses the ThemeEditor preview).
	const renderPreview = (name, label) => {
		const src = getPath(formData, name)?.fileData || "";
		return src ? <img src={src} alt={label} style={{ height: "40px", width: "auto", maxWidth: "80px", objectFit: "contain" }} /> : null;
	};
	const previewRow = { display: "flex", alignItems: "center", gap: "0.5rem" };

	if (isUpdate && permissionsFetched && userDetail && !canOpenEdit) {
		return <Navigate to="/not-authorized" replace />;
	}

	if (needsFetch && !initializedRef.current) {
		return (
			<div className="contentpanel">
				<Loader />
			</div>
		);
	}

	return (
		// <Dialog size="md" open={open} title={"Add Company"} SubmitText={updateData ? "Update" : "Submit"} handleClose={handleClose} handleSubmit={updateData ? handleUpdateSubmit : handleFormSubmit}>
		<div className="contentpanel">
			<div className="cust-row flex-algn-cent">
				<div className="cust-col-5">
					<h1 className="title">{isUpdate ? "Update Company" : "Add Company"}</h1>
				</div>
				<div className="cust-col-5 flex-jc-end">
					<Link to="/companies" className=" ">
						<Button className="" text="Go Back" style={buttonClasses.lynkitOrangeEmpty} />
					</Link>
				</div>
			</div>
			<hr className="bgg" />

			{isUpdate ? (
				<Tabs
					value={activeTab}
					onChange={handleTabChange}
					aria-label="company edit tabs"
					sx={{
						mb: 3,
						"& .MuiTabs-indicator": { backgroundColor: "var(--primary-color)" },
						"& .MuiTab-root": { color: "var(--text-color)" },
						"& .MuiTab-root.Mui-selected": { color: "var(--primary-color)" },
					}}
				>
					<Tab label={detailsDirty ? "Details •" : "Details"} value="details" id="company-tab-details" aria-controls="company-tabpanel-details" />
					<Tab label={themeDirty ? "Theme •" : "Theme"} value="theme" id="company-tab-theme" aria-controls="company-tabpanel-theme" />
				</Tabs>
			) : null}

			{/* Both tabs stay mounted (only hidden) so switching never loses either draft. */}
			<div role={isUpdate ? "tabpanel" : undefined} id={isUpdate ? "company-tabpanel-details" : undefined} aria-labelledby={isUpdate ? "company-tab-details" : undefined} hidden={isUpdate && activeTab !== "details"}>
			<form className="">
				<div className="form-inputs mt-2">
					<Input
						readOnly={!!updateData}
						error={errors?.orgName}
						label="Company Name"
						placeholder="Enter Company Name"
						name="orgName"
						value={formData.orgName}
						onChange={handleInput}
						required={true}
					/>
					<Input
						readOnly={!!updateData}
						error={errors?.representativeName}
						label="Name"
						placeholder="Enter Name"
						name="representativeName"
						value={formData.representativeName}
						onChange={handleInput}
						required={true}
					/>
					<Input
						readOnly={!!updateData}
						error={errors?.representativeMobile}
						label="Mobile"
						placeholder="Enter Mobile"
						name="representativeMobile"
						value={formData.representativeMobile}
						onChange={handleInput}
						required={true}
					/>
					<Input
						readOnly={!!updateData}
						error={errors?.representativeEmail}
						type="email"
						label="Email"
						placeholder="Enter Email"
						name="representativeEmail"
						value={formData.representativeEmail}
						onChange={handleInput}
						required={true}
					/>
					<Input readOnly={!!updateData} error={errors?.address} label="Address" placeholder="Enter Address" name="address" value={formData.address} onChange={handleInput} required={true} />
					{/* <Input readOnly={!!updateData} error={errors?.state} label="State" placeholder="Enter State" name="state" value={formData.state} onChange={handleInput} required={true} /> */}
					<Dropdown id="state" label="State" placeholder="Select State" name="state" options={statesOptions} value={stateValue} onChange={onDropdownChange} readonly={!!updateData} error={errors?.state} required/>
					<Input readOnly={!!updateData} error={errors?.city} label="City" placeholder="Enter City" name="city" value={formData.city} onChange={handleInput} required={true} />
					{/* <Dropdown
						id="city"
						name="city"
						label="Select City"
						onChange={onDropdownChange}
						options={[]}
						value={{
							value: formData.orgId,
							label:
								orgs.find((_) => _._id == formData.orgId)
									?.name || "",
						}}
					/> */}
					<Input readOnly={!!updateData} error={errors?.pincode} label="Pincode" placeholder="Enter Pincode" name="pincode" value={formData.pincode} onChange={handleInput} required={true} />
					<Input readOnly={!!updateData} error={errors?.pan} label="PAN" placeholder="Enter PAN" name="pan" value={formData.pan} onChange={handleInput} required={true} />
					<Input readOnly={!!updateData} error={errors?.gstin} label="GST" placeholder="Enter GST" name="gstin" value={formData.gstin} onChange={handleInput} required={true} />
					{!updateData ? (
						<Checkbox
							name="customizeCheckpoint"
							checked={customizeCheckpoint}
							onChange={(e) => {
								setCustomizeCheckpoint(e.target.checked);
								if (e.target.checked) {
									setFormData((o) => ({ ...o, checkpoints: DEFAULT_CHEKPOINTS }));
								} else {
									setFormData((o) => {
										let n = { ...o };
										delete n.checkpoints;
										return n;
									});
								}
							}}
							label="Customize Checkpoints"
						/>
					) : null}
				</div>
				{!updateData && customizeCheckpoint ? (
					<div className="theme-container">
						<h6>Checkpoints</h6>
						<div style={{ display: "flex", gap: "0.5rem", flexDirection: "column", marginTop: "0.75rem" }}>
							{formData.checkpoints.map(({ displayName, checkpointType }, i) => {
								return (
									<div key={i} style={{ display: "grid", gap: "1rem", gridTemplateColumns: "30% 30% 10%" }}>
										<Input
											name="displayName"
											label="Display Name"
											value={displayName}
											onChange={(e) => {
												setFormData((o) => ({
													...o,
													checkpoints: o.checkpoints.map((_, j) => {
														if (i == j) {
															return { ..._, [e.target.name]: e.target.value };
														}
														return _;
													}),
												}));
											}}
										/>
										<Dropdown
											name="checkpointType"
											label="Checkpoint Type"
											value={checkpointTypeOptions.find((_) => _.value == checkpointType) || null}
											onChange={(opt, name) => {
												setFormData((o) => ({
													...o,
													checkpoints: o.checkpoints.map((_, j) => {
														if (i == j) {
															return { ..._, [name]: opt?.value || "" };
														}
														return _;
													}),
												}));
											}}
											options={checkpointTypeOptions}
										/>
										<AddRemove
											list={formData.checkpoints}
											filterMethod={(_) => _.displayName == "" && _.checkpointType == ""}
											onAdd={() => {
												setFormData((o) => ({ ...o, checkpoints: [...o.checkpoints, { displayName: "", checkpointType: "" }] }));
											}}
											onRemove={(i) => {
												setFormData((o) => ({ ...o, checkpoints: o.checkpoints.filter((_, j) => j != i) }));
											}}
											index={i}
											outerIndex={i}
										/>
									</div>
								);
							})}
						</div>
					</div>
				) : null}
				<div className="theme-container">
					<div className="flex-row-sb mb-2">
						<div style={{ fontWeight: "600" }}>Permissions</div>
						<Checkbox
							label="Select All"
							id="selectAllPerm"
							name="selectAllPerm"
							checked={Object.keys(formData?.permissions || {})?.length == Object.keys(orgPerms || {})?.length}
							onChange={handleCheckAllPerm}
							disabled={permsLocked}
						/>
					</div>
					<div className="perm_wrap">{perms}</div>
				</div>

				{!isUpdate ? (
				<div className="theme-container">
					<h6>Theme Setting</h6>
					<div className="form-inputs">
						<Input
							label="Title"
							placeholder="Enter Title"
							name="title"
							value={formData.title}
							// error={errors?.title}
							onChange={(e) => handleThemeChange(e.target.name, e.target.value)}
							// required={true}
						/>
						<div style={previewRow}>
							<div style={{ flex: 1, minWidth: 0 }}>
								<UploadFileButton
									allowedExtensions=".jpg, .jpeg, .png, .webp"
									name="logo"
									fileName={displayFileName(formData.logo?.fileName)}
									onChange={(file) => handleThemeChange("logo", file)}
									onChangeClear={() => handleThemeChange("logo", "")}
									id="uploadFileforLogo"
									buttonText="Upload Logo "
									// error={errors?.logo}
								/>
							</div>
							{renderPreview("logo", "Logo")}
						</div>
						<div style={previewRow}>
							<div style={{ flex: 1, minWidth: 0 }}>
								<UploadFileButton
									allowedExtensions=".png, .ico"
									name="favIcon"
									fileName={displayFileName(formData.favIcon?.fileName)}
									onChange={(file) => handleThemeChange("favIcon", file)}
									onChangeClear={() => handleThemeChange("favIcon", "")}
									id="uploadFileforFavIcon"
									buttonText="Upload Favicon "
									// error={errors?.favIcon}
								/>
							</div>
							{renderPreview("favIcon", "Favicon")}
						</div>
						{Object.keys(formData.statIcon).map((key, i) => {
							let statIcon = formData.statIcon[key];
							const label = `${capitalizeCamelCase(key)} Icon`;
							return (
								<div key={i} style={previewRow}>
									<div style={{ flex: 1, minWidth: 0 }}>
										<UploadFileButton
											allowedExtensions=".jpg, .jpeg, .png, .webp"
											name={`statIcon.${key}`}
											fileName={displayFileName(statIcon.fileName)}
											onChange={(file) => handleThemeChange(`statIcon.${key}`, file)}
											onChangeClear={() => handleThemeChange(`statIcon.${key}`, "")}
											id={`statIcon.${key}`}
											buttonText={`Upload ${label}`}
										/>
									</div>
									{renderPreview(`statIcon.${key}`, label)}
								</div>
							);
						})}
						<Checkbox
							name="selectedTheme"
							label="Dark Mode"
							checked={formData.theme.selectedTheme === "darkMode" ? true : false}
							onChange={(e) => handleThemeChange(e.target.name, e.target.checked, "darkMode")}
						/>
						<Checkbox
							name="selectedTheme"
							label="Light Mode"
							checked={formData.theme.selectedTheme === "lightMode" ? true : false}
							onChange={(e) => handleThemeChange(e.target.name, e.target.checked, "lightMode")}
						/>
						<ColorPicker name={"primaryColor"} value={formData.theme.primaryColor} onChange={(color) => handleThemeChange("primaryColor", color)} />
					</div>
				</div>
				) : null}

				<div className="theme-container">
					<h6>HHT IMEI</h6>
					<div style={{ display: "flex", gap: "0.5rem", flexDirection: "column" }}>
						{formData.hhtImei.map(({ imei }, i) => {
							return (
								<div key={i} style={{ display: "flex", gap: "1rem" }}>
									<Input
										name="imei"
										label="IMEI"
										value={imei}
										onChange={(e) => {
											setFormData((o) => ({
												...o,
												hhtImei: o.hhtImei.map((_, j) => {
													if (i == j) {
														return { ..._, [e.target.name]: e.target.value };
													}
													return _;
												}),
											}));
										}}
									/>
									<AddRemove
										list={formData.hhtImei}
										filterMethod={(_) => _.imei == ""}
										onAdd={() => {
											setFormData((o) => ({ ...o, hhtImei: [...o.hhtImei, { imei: "" }] }));
										}}
										onRemove={(i) => {
											setFormData((o) => ({ ...o, hhtImei: o.hhtImei.filter((_, j) => j != i) }));
										}}
										index={i}
										outerIndex={i}
									/>
								</div>
							);
						})}
					</div>
				</div>

				<div className="d-flex justify-content-center" style={{ height: "79px" }}>
					<Button text={isUpdate ? "Save Details" : "Submit"} style={{ ...buttonClasses.lynkitOrangeFill, width: "15%" }} onClick={isUpdate ? handleUpdateSubmit : handleFormSubmit} disabled={isSubmitting} />
				</div>
			</form>
			</div>

			{isUpdate ? (
				<div role="tabpanel" id="company-tabpanel-theme" aria-labelledby="company-tab-theme" hidden={activeTab !== "theme"}>
					<ThemeEditor scope="org" orgId={editOrgId} canEdit={canEditOrgTheme} onDirtyChange={setThemeDirty} />
				</div>
			) : null}
		</div>
	);
};

// Keyed by the org id so moving between two edit URLs starts a fresh form (no state from the
// previous org can be saved against the new one).
const AddCompany = () => {
	const { id } = useParams();
	return <AddCompanyForm key={id || "new"} />;
};

export default AddCompany;
